import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { ExportButton } from '@/components/common/ExportButton';
import type { AnatelCitationLine, AnswerOut, AnatelCitationLines } from '@/services/types';

/** Doctrine lines drawn before the remainder is counted and exportable. */
const LINES_SHOWN = 25;

/**
 * The outcome record of a decision track, organised by the ground a decision
 * cites. Shared between PADO (`decisions.doctrine` keys, `overview.outcomes`
 * labels, priced-fine counts) and PAF (`pafList.doctrine` keys, `paf.outcomes`
 * labels, disputed-credit counts): the i18n prefixes and the money column are
 * the only track-specific parts, so they arrive as props.
 */
export function DoctrinePanel({
  answer,
  selected,
  onSelectCitation,
  exportPath,
  i18nPrefix,
  outcomeKeyPrefix,
}: {
  answer: AnswerOut<AnatelCitationLines>;
  selected: string | null;
  onSelectCitation: (citation: string) => void;
  exportPath: string;
  i18nPrefix: string;
  outcomeKeyPrefix: string;
}) {
  const { t, i18n } = useTranslation(['anatel']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <section className="rounded-lg border bg-card p-4">
      <Answer answer={answer} help={t(`anatel:${i18nPrefix}.help`)}>
        {(value) => (
          <div className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold text-foreground">
                  {t(`anatel:${i18nPrefix}.title`)}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t(`anatel:${i18nPrefix}.intro`)}
                </p>
              </div>
              <ExportButton path={exportPath} />
            </div>
            <ul className="space-y-2">
              {value.lines.slice(0, LINES_SHOWN).map((line) => (
                <DoctrineLineRow
                  key={line.citation}
                  line={line}
                  selected={line.citation === selected}
                  onSelect={() => onSelectCitation(line.citation)}
                  i18nPrefix={i18nPrefix}
                  outcomeKeyPrefix={outcomeKeyPrefix}
                />
              ))}
            </ul>
            {value.lines.length > LINES_SHOWN ? (
              <p className="text-xs text-muted-foreground">
                {t(`anatel:${i18nPrefix}.showing`, {
                  shown: number.format(LINES_SHOWN),
                  total: number.format(value.lines.length),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}

function DoctrineLineRow({
  line,
  selected,
  onSelect,
  i18nPrefix,
  outcomeKeyPrefix,
}: {
  line: AnatelCitationLine;
  selected: boolean;
  onSelect: () => void;
  i18nPrefix: string;
  outcomeKeyPrefix: string;
}) {
  const { t, i18n } = useTranslation(['anatel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  return (
    <li
      className={
        selected
          ? 'space-y-1.5 rounded border border-primary/60 bg-primary/5 p-2.5'
          : 'space-y-1.5 rounded border p-2.5'
      }
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        {/* The ground is the drill-through: a count on this panel reaches the
            decisions it counts (no orphan figures). */}
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          className="text-left text-xs font-medium text-primary underline-offset-4 hover:underline"
        >
          {line.citation}
        </button>
        <span className="text-xs tabular-nums text-muted-foreground">
          {t(`anatel:${i18nPrefix}.decisions`, { count: line.decisions })}
        </span>
      </div>
      {/* Fine rows counted, never summed: the sanctions page owns the
          citation-conditioned distribution. Null on tracks with no fines. */}
      {line.fine_rows != null && line.fine_rows > 0 ? (
        <p className="text-xs tabular-nums text-muted-foreground">
          {t(`anatel:${i18nPrefix}.fined`, {
            fined: line.fined_decisions,
            rows: line.fine_rows,
          })}
        </p>
      ) : null}
      {/* PAF money is a disputed tax credit: counted for coverage, never
          summed, never a fine. Null on PADO. */}
      {line.credited_decisions != null ? (
        <p className="text-xs tabular-nums text-muted-foreground">
          {t(`anatel:${i18nPrefix}.credited`, {
            credited: line.credited_decisions,
          })}
        </p>
      ) : null}
      <ul className="flex flex-wrap gap-1">
        {line.outcomes.map((outcome) => (
          <li
            key={outcome.code}
            className="rounded border bg-muted/40 px-1.5 py-0.5 text-xs text-foreground"
          >
            {t(`${outcomeKeyPrefix}.${outcome.code}`, { defaultValue: outcome.code })}
            <span className="ml-1 tabular-nums text-muted-foreground">
              {number.format(outcome.count)} · {percent.format(outcome.share)}
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}
